/**
 * THE audit-advisory predicate and finding list (2026-08-22).
 *
 * One definition, consumed by every surface that discloses or counts a
 * report's open audit: the report page's header status chip (via
 * draftTierUx — a banner until 2026-10-13, when report pages lost their
 * banners), the DOCX amber banner, the PPTX approval state, the printed-PDF
 * closing tail, the
 * plan's auditRecommended flag, and the composite's confidence discount.
 * The final pre-deploy review found FOUR different predicates and THREE
 * different counts across those surfaces — a report could read "1 finding"
 * at the top of the page and "5 findings" at the bottom of the same page.
 *
 * Flag: a stamped hold, a reviewer needs-revision verdict, OR blocking truth
 * violations in qaFindings (the pre-hold-era shape and the shape produced
 * when audits append findings after generation — delivery admits it, so the
 * disclosure must cover it).
 *
 * Findings: the re-evaluated stamped findings (duplicates collapsed,
 * superseded rules dropped — reevaluateStampedHoldFindings), then the
 * blocking truth violations standing in `qaFindings` itself, then reviewer
 * issues under a needs-revision verdict. Count falls back to the open
 * truth-question count, then 1, so a flagged report never discloses "0".
 *
 * SUPERSESSION IS ERA-INDEPENDENT. The stamped list and the blocking
 * `qaFindings` list are the SAME rows — `resolveQualityHold` stamps
 * `qualityHoldFindings` from `summarizeQaSeverity().blocking`. So a stamp
 * that today's rules no longer produce is still sitting in `qaFindings` on
 * the immutable revision, and reading that bucket without re-evaluating it
 * hands the superseded finding straight back (the Redhead stated-precision
 * case: '3 km' against a governed 2.9). Both lists run through the one
 * `reevaluateStampedHoldFindings` — a second supersession rule set would be
 * a second way for the two to disagree.
 *
 * That applies to APPENDICES too, not just the counted banners: the DOCX and
 * PPTX "Quality Findings" sections print rows without a count beside them, so
 * drift there is invisible to any test that only asserts a missing number.
 * They take `liveQualityHoldFindings` — the same resolution, exported.
 *
 * Every source the FLAG reads must also be able to supply the COUNT. A source
 * that can raise the flag but not the number leaves the disclosure on the
 * floor-of-1 `'flagged'` branch — the vaguest sentence the surfaces have — on
 * exactly the reports this exists for.
 *
 * `kind` travels with the count for the surfaces that put it in a SENTENCE,
 * and those surfaces TAKE BOTH: a builder that states the live audit's tally
 * accepts the resolved `{count, kind}` pair, never a bare number. Adding a
 * second count to get a truer sentence is the eliminated defect class above;
 * branch on `kind` instead. The in-app export dialog (ReportHeaderStrip.ExportAuditWarningDialog)
 * branches the same way.
 *
 * THE FLOOR IS NOT A FINDING, ON ANY SURFACE (2026-08-25, round 8). A `count`
 * of 1 that itemises nothing is a FLAG, not a tally, and the four surfaces
 * disagreed about it for a full round: the reader and printed PDF said "1
 * quality finding is open on this analysis" while the counsel DOCX and the deck
 * said no individual finding stands. Both halves came from the same shape — a
 * surface that had thrown `kind` away and passed a bare number. Nothing that
 * states the audit's STATE takes one any more:
 *
 *   - `openAuditDisclosureSentence({count, kind})` — the document-facing
 *     sentence (printed PDF + web reader tail, counsel DOCX cover, deck
 *     governance table).
 *   - `openAuditBannerSummary({count, kind}, audience)` — the count-in-a-TITLE
 *     clause (reader banner via draftTierUx, main DOCX amber cover heading).
 *     `audience` dates the rules on the `'flagged'` branch and changes nothing
 *     else; see BANNER_RULE_MOMENT.
 *   - `ReportClosingTail`'s `audit` prop — the resolved pair only; the numeric
 *     arm that silently relabelled the floor as an itemised finding is gone.
 *
 * THE BARE-NUMBER RULE IS A CRITERION, NOT AN EXCEPTION LIST (corrected round
 * 9). Round 8 wrote it as "`openAuditListNote` alone", which was already false
 * in this same file: `frozenQualityDecisionCaption(frozenCount, surface, live)`
 * also takes a bare count into a sentence. Neither is an exception, because
 * `kind` describes the LIVE audit resolution — wherever a copy of it travels
 * (the plan item's `auditKind`, a component prop), it is still that
 * resolution's, and nothing else has one. The criterion:
 *
 *   A builder may take a bare number when the number it names is NOT the live
 *   resolution's tally — when no value of `kind` could make its sentence false.
 *
 *   - `openAuditListNote(total, shown)` states ARITHMETIC over a list the
 *     caller already rendered.
 *   - `frozenQualityDecisionCaption(frozenCount, …)` counts rows of the
 *     REPLAY-LOCKED frozen list, reproduced verbatim and never re-evaluated —
 *     a different list from the live one, which is the whole point of the
 *     caption.
 *
 * Anything that states the LIVE audit's STATE takes the `{count, kind}` pair.
 */
import type { SiteRagReport } from '../../types/siteRag';
import { containsTruthViolation, summarizeQaSeverity } from './qaSeverityPolicy';
import { reevaluateStampedHoldFindings } from './reevaluateQualityHold';
import { truthQuestionCountOf } from '../decisionAnalysis/lifecyclePresentation';
import { isWithheldNarrativeHoldFinding } from '../report/withheldNarrative';

export function reportHasOpenAuditFlag(
  report: Pick<SiteRagReport, 'qualityHold' | 'qualityReview' | 'qaFindings'>,
): boolean {
  return (
    report.qualityHold === 'needs-revision'
    || report.qualityReview?.verdict === 'needs-revision'
    || containsTruthViolation(report.qaFindings)
  );
}

/**
 * What `count` MEASURES — so a disclosure can name it without overclaiming.
 *
 * The count is single-sourced by design, but its NOUN is not interchangeable:
 * "N statements disagree with the site evidence" is false when N counts a
 * reviewer's section notes, and false again when nothing itemizable stands
 * and N is only the never-disclose-zero floor. A surface that puts the count
 * in a sentence must branch on this instead of inventing a second count.
 */
export type OpenAuditKind =
  /** Statements the site evidence contradicts — truth-gate stamps / open truth questions. */
  | 'truth-disagreement'
  /** Itemised reviewer issues under a needs-revision verdict. */
  | 'audit-finding'
  /** Flagged, but no finding today's rules still produce — `count` is the floor of 1. */
  | 'flagged';

export interface OpenAuditFindings {
  flag: boolean;
  /**
   * Deduplicated, currently-standing finding strings (may be empty), capped
   * at `OPEN_AUDIT_MAX_FINDINGS`. Consumers render this as a bulleted list.
   */
  findings: readonly string[];
  /**
   * How many findings STAND — never 0 when `flag` is true, and deliberately
   * NOT `findings.length`: the list is capped, the number is not, so
   * `count > findings.length` is the truncation showing rather than hiding.
   */
  count: number;
  /** What `count` counts. `'flagged'` whenever `flag` is false. */
  kind: OpenAuditKind;
}

/**
 * Ceiling on the DISCLOSED list — mirrors `QUALITY_HOLD_MAX_FINDINGS`
 * (runTypedReportGeneration.ts), the bound `qualityHoldFindings` are written
 * under. The stamped list inherits that cap upstream; the `qaFindings` and
 * reviewer lists are read live and are bounded only here, so without it a
 * warn-mode report with sixty blocking violations renders sixty bullets on
 * the DOCX cover. Held privately: a surface that wants to say "showing 20 of
 * 60" gets it from `count` vs `findings.length`, not from importing a number.
 */
const OPEN_AUDIT_MAX_FINDINGS = 20;

/**
 * One shape for every populated branch: the list is bounded, the count is the
 * honest total, and the two are resolved together (a surface that resolves
 * them independently is how N reviewer notes get asserted as N untruths).
 */
function disclose(findings: readonly string[], kind: OpenAuditKind, totalCount?: number): OpenAuditFindings {
  return {
    flag: true,
    findings: findings.slice(0, OPEN_AUDIT_MAX_FINDINGS),
    count: Math.max(findings.length, totalCount ?? findings.length),
    kind,
  };
}

/**
 * THE stamped-hold list every surface may print — re-evaluated, never raw.
 *
 * `report.qualityHoldFindings` is a frozen snapshot of the truth gate AT
 * GENERATION. Reading it directly prints findings today's rules explicitly
 * supersede (the Redhead stated-precision class: prose says '3 km', the
 * governed value is 2.9, and `roundsToStatedPrecision` now accepts it) plus
 * every byte-identical repeat. The reader banner and the DOCX/PDF cover
 * banners route through `openAuditFindings`, which re-evaluates; the DOCX and
 * PPTX **appendices** bulleted the raw array, so the same report disclosed one
 * live finding on its cover and five on its "Quality Findings" slide.
 *
 * Exported so those appendices consume the SAME resolution the count does —
 * `openAuditFindings` below calls it too, so the two cannot drift. Uncapped by
 * design: the array is already bounded at QUALITY_HOLD_MAX_FINDINGS upstream,
 * and an appendix paginates rather than truncates.
 */
export function liveQualityHoldFindings(report: SiteRagReport): readonly string[] {
  const stamped = (Array.isArray(report.qualityHoldFindings) ? report.qualityHoldFindings : [])
    .filter((finding): finding is string => typeof finding === 'string' && finding.trim().length > 0);
  return reevaluateStampedHoldFindings(report, stamped).findings;
}

export function openAuditFindings(report: Omit<SiteRagReport, 'decisionAnalysis'>): OpenAuditFindings {
  const flag = reportHasOpenAuditFlag(report);
  if (!flag) return { flag: false, findings: [], count: 0, kind: 'flagged' };
  const stampedLive = liveQualityHoldFindings(report);
  /*
   * Blocking truth violations carried by `qaFindings` itself — the pre-hold
   * era's shape, and the one the audit writes when it annotates a report
   * AFTER generation (grounding warn/on mode stamps no hold findings at all).
   * `reportHasOpenAuditFlag` already reads them, so they must be countable
   * here too, and they are the same class of thing as a stamped finding: a
   * statement the site evidence contradicts.
   *
   * Formatted `detector: message` — the shape the hold stamps use — then run
   * through the SAME re-evaluation the stamps get. These rows are the source
   * `resolveQualityHold` stamped FROM, so skipping it would resurrect every
   * finding the stamped path supersedes (and would drop the dedup: one wrong
   * figure repeated across sections is one thing wrong, not several).
   */
  const annotated = reevaluateStampedHoldFindings(
    report,
    summarizeQaSeverity(report.qaFindings).blocking
      .map(({ detector, message }) => `${detector}: ${message}`),
  ).findings;
  const reviewer = report.qualityReview?.verdict === 'needs-revision'
    ? (() => {
        const normalized = (report.qualityReview.issues ?? [])
          .map((issue, idx) => {
            if (!issue || typeof issue !== 'object') return null;
            const rec = issue as {
              section?: unknown;
              issue?: unknown;
              description?: unknown;
              message?: unknown;
            };
            const text =
              typeof rec.issue === 'string' && rec.issue.trim().length > 0
                ? rec.issue.trim()
                : typeof rec.description === 'string' && rec.description.trim().length > 0
                  ? rec.description.trim()
                  : typeof rec.message === 'string' && rec.message.trim().length > 0
                    ? rec.message.trim()
                    : '';
            if (!text) return null;
            const section =
              typeof rec.section === 'string' && rec.section.trim().length > 0
                ? rec.section.trim()
                : `issue-${idx + 1}`;
            return `${section}: ${text}`;
          })
          .filter((entry): entry is string => Boolean(entry));
        if (normalized.length > 0) return normalized;
        const declaredCount =
          typeof report.qualityReview.issueCount === 'number'
          && Number.isFinite(report.qualityReview.issueCount)
            ? Math.max(0, Math.trunc(report.qualityReview.issueCount))
            : 0;
        if (declaredCount > 0 && Array.isArray(report.qualityReview.issues) && report.qualityReview.issues.length > 0) {
          return Array.from(
            { length: declaredCount },
            (_, idx) =>
              `qualityReview: Quality review recorded ${declaredCount} unresolved issue${declaredCount === 1 ? '' : 's'} (issue #${idx + 1} detail missing in stored report).`,
          );
        }
        return [];
      })()
    : [];
  /*
   * A WITHHELD-NARRATIVE stamp (reviewer-voice gate, 2026-09-18) says a
   * narrative was withheld by an automated wording check — NOT that any
   * statement disagrees with the site evidence (nothing untrue was said;
   * the leaf is simply not in the report). It is an audit finding, and it
   * must never be counted into a "disagrees with the site evidence" tally.
   */
  const stampedTruth = stampedLive.filter((finding) => !isWithheldNarrativeHoldFinding(finding));
  const stampedWithheld = stampedLive.filter(isWithheldNarrativeHoldFinding);
  const annotatedTruth = annotated.filter((finding) => !isWithheldNarrativeHoldFinding(finding));
  const rawStampedCount = Array.isArray(report.qualityHoldFindings)
    ? report.qualityHoldFindings.filter((f): f is string => typeof f === 'string' && f.trim().length > 0).length
    : 0;
  // When qualityHoldFindings hit QUALITY_HOLD_MAX_FINDINGS (20) upstream,
  // stampedTruth was truncated at generation time; merge standing annotatedTruth
  // and use the uncapped standing truth-disagreement count so the export warning
  // and cover banner match the report's truthQuestions count.
  const effectiveStampedTruth =
    rawStampedCount >= OPEN_AUDIT_MAX_FINDINGS
      ? Array.from(new Set([...stampedTruth, ...annotatedTruth]))
      : stampedTruth;
  const uncappedStampedTotal =
    rawStampedCount >= OPEN_AUDIT_MAX_FINDINGS
      ? Math.max(effectiveStampedTruth.length, annotatedTruth.length, truthQuestionCountOf(report) ?? 0)
      : stampedTruth.length;
  // Count and kind are resolved together: whichever source supplies the
  // number also names it. Resolving them independently is how a surface ends
  // up asserting that N reviewer notes are N untrue statements.
  if (effectiveStampedTruth.length > 0) return disclose(effectiveStampedTruth, 'truth-disagreement', uncappedStampedTotal);
  if (annotated.length > 0) return disclose(annotated, 'truth-disagreement');
  if (stampedWithheld.length > 0 || reviewer.length > 0) {
    return disclose([...stampedWithheld, ...reviewer], 'audit-finding');
  }
  const truthQuestions = truthQuestionCountOf(report) ?? 0;
  return truthQuestions > 0
    ? { flag: true, findings: [], count: truthQuestions, kind: 'truth-disagreement' }
    : { flag: true, findings: [], count: 1, kind: 'flagged' };
}

/**
 * THE truncation sentence — one wording for the reader banner, the printed
 * PDF, the DOCX cover and the deck, so the four surfaces cannot drift and a
 * recipient counting bullets can reconcile them with the heading instead of
 * concluding rows were lost in conversion.
 *
 * `total` must be the count the SAME resolution produced (`openAuditFindings`,
 * or the barred quality-hold branch's own re-evaluated length) — never a
 * number the surface derives for itself; a second count is the eliminated
 * defect class this module exists to close.
 *
 * `shown` is what the caller actually RENDERED, which on the page banner is
 * two truncations deep: `findings` is capped at OPEN_AUDIT_MAX_FINDINGS here,
 * and the banner then lists the first five of those. Passing `findings.length`
 * rather than the rendered length is how "60 findings to review" came to sit
 * above five bullets and "+15 more", silently dropping forty.
 *
 * Null when nothing is hidden, and null when nothing is listed at all: a
 * surface showing no list makes no claim about one, and "Showing 0 of 1"
 * reads as a defect rather than a disclosure.
 *
 * ─── IT NAMES NO DESTINATION AND NO ACTION, ON PURPOSE ────────────────
 *
 * This sentence used to end "review the rest on the report's Quality tab in
 * the platform". VERIFIED 2026-08-25: there is no such destination, and the
 * pointer was worse than the silent drop it replaced — it sent the reader
 * somewhere that contradicted the banner they had just read.
 *
 *   - "Review quality details" routes to `/sites/:id?tab=audit&report=<id>`
 *     (SiteRagReportView.openQualityAudit) — the Audit tab's Report-quality
 *     block (ReportQualityAuditSection). Nothing there is labelled
 *     "Quality tab" any more either.
 *   - That block renders exactly two finding surfaces: `QualityPanel`
 *     (qualityReview / factCheck / evidenceConfidence / SED scores — it
 *     prints `qualityReview.issueCount` and itemises NO issue) and
 *     `SanityCheckCard`, which lists `report.sanityFindings`, a different
 *     list with a different count.
 *   - Neither reads `qaFindings` blocking violations nor
 *     `qualityHoldFindings`. On the probe report (grounding warn mode, 60
 *     blocking violations, no hold stamp, no reviewer verdict) that surface
 *     itemises zero of them and reads "0 issues flagged".
 *   - The one surface that DOES itemise truth disagreements —
 *     TruthResolutionDialog, over `qaFindings.truthQuestions` — is fed only
 *     by `evaluateQualityHold` (runTypedReportGeneration), so a warn-mode
 *     report has no truth questions at all.
 *
 * The REPLACEMENT — "rerun the report to re-check all N" — traded a false
 * destination for a false action, on both counts (2026-08-25 round 6):
 *
 *   - It is printed on the DOCX cover, a customer-facing artifact whose
 *     recipient has no platform account and cannot rerun anything. An
 *     instruction the reader cannot execute is not a disclosure.
 *   - "re-check all N" overstates what a rerun does. A rerun REGENERATES the
 *     narrative and runs the gate over the NEW prose; the N findings stamped
 *     against the OLD prose are discarded with it, never re-checked. A rerun
 *     that produced a clean report would not mean those N were resolved.
 *   - On the reader banner the action was not even offered: for
 *     'quality-hold' and 'audit-recommended' DraftBanner's resolver returns
 *     the "Review quality details" CTA whenever `onReviewQuality` is wired,
 *     which every caller in the repo did. (Since 2026-10-13 that CTA is a
 *     report-header button and DraftBanner is unmounted.)
 *
 * So the sentence states the ARITHMETIC and stops. It names no destination
 * (none itemises the remainder), no action (none acts on the remainder), and
 * not even the remainder itself — a number the reader can act on nowhere
 * invites the pointer this sentence twice lost. The DIRECTION a document
 * recipient can act on lives in `openAuditDisclosureSentence` below, which is
 * addressed to that audience and promises only what it can deliver.
 *
 * If a surface is ever built that itemises the full set, name it HERE — one
 * sentence, four surfaces.
 */
export function openAuditListNote(total: number, shown: number): string | null {
  if (!Number.isFinite(total) || !Number.isFinite(shown)) return null;
  if (shown <= 0 || total <= shown) return null;
  // `total > shown >= 1` on every returned sentence, so both nouns are plural.
  return `Showing ${shown} of ${total} findings.`;
}

/**
 * THE audit-recommended disclosure — for surfaces read as a DOCUMENT.
 *
 * Two emitters printed this sentence independently and neither shared a
 * character with the other: `ReportClosingTail` (the web reader's closing tail
 * AND the printed PDF, one shared DOM) and `buildSiteReportDocx`'s counsel
 * cover (`lib/export/docxBuilder.ts`). Round 5 fixed the wording in
 * `openAuditListNote` and claimed all four surfaces moved; these two kept
 * printing "review them in the platform before relying on this document"
 * verbatim, because they never called it.
 *
 * EVERY CALLER, so the next change is one edit and not another hunt:
 *
 *   - `components/siterag/report/ReportClosingTail.tsx` — web reader + printed
 *     PDF (one DOM, rendered once by SiteRagReportView).
 *   - `lib/export/docxBuilder.ts` — `buildSiteReportDocx` counsel cover, the
 *     second DOCX builder behind POST /api/export/site-report-docx.
 *   - `services/reportPptxExport.ts` — the deck's Report Governance table,
 *     which previously stated the audit STATE and no number at all.
 *
 * `services/reportDocxExport.ts` (the main DOCX) deliberately does NOT use it:
 * its cover is an amber banner that ITEMISES the findings, so it carries the
 * heading + bullets + `openAuditListNote` instead. Adding this sentence there
 * too would state the count twice in one block.
 *
 * ─── WHY THIS IS NOT THE APP'S WORDING ────────────────────────────────
 *
 * "Review them in the platform" is false for this audience twice over. The
 * recipient of a counsel DOCX or a printed PDF is not a platform user — there
 * is nothing for them to open — and even a user who did open it lands on a
 * surface that itemises none of these findings (see the destination audit on
 * `openAuditListNote`). So this sentence names no platform, no tab and no
 * rerun, and gives the one direction a document recipient can actually
 * execute: ask the party that issued the document.
 *
 * It states the count and NOTHING that would need a second one. The argument
 * must be the resolved `openAuditFindings(report)` — the same resolution the
 * reader banner, the DOCX cover heading and the deck's governance table use —
 * and it is taken as a `{count, kind}` PAIR rather than a number so a caller
 * cannot hand over a count whose noun it has thrown away. `kind` decides
 * whether the sentence may name a number at all: on the floor-of-1
 * `'flagged'` branch no finding stands to be counted.
 *
 * ─── IT ITEMISES NOTHING, AND CLAIMS NOTHING ABOUT WHO DOES ───────────
 *
 * Deliberately silent on where the findings ARE listed, because that differs
 * per emitter and a shared sentence cannot be true about all of them: the
 * printed PDF lists none (the reader's delivery state — once DraftBanner,
 * since 2026-10-13 the header status chip — is `print:hidden` chrome), the
 * counsel DOCX has an "Audit Findings" table fed by `collectCounselAuditFindings`
 * — `qaFindings` block/warn rows only, so it is EMPTY on a report flagged by a
 * hold stamp or a reviewer verdict alone — and the main DOCX cover bullets a
 * capped slice. Naming any of them here would be false on at least one.
 *
 * Null at zero / non-finite so a clean report can never grow an invented
 * notice from a stray `0`.
 */
export function openAuditDisclosureSentence(
  audit: Pick<OpenAuditFindings, 'count' | 'kind'>,
): string | null {
  const { count, kind } = audit;
  if (!Number.isFinite(count) || count <= 0) return null;
  /*
   * THE FLOOR IS NOT A FINDING (2026-08-25, round 7).
   *
   * `'flagged'` means the report is flagged and NOTHING today's rules still
   * produce stands against it — `findings` is empty and `count` is the
   * never-disclose-zero floor of 1, not a tally. Printing "1 quality finding is
   * open" there asserts an itemisable finding the document cannot produce and
   * the issuer cannot show, so this branch states the flag and no number. The
   * count is still required above: a clean report resolves to count 0 and this
   * builder stays silent rather than growing a notice from a stray flag.
   */
  if (kind === 'flagged') {
    return `Audit recommended — this analysis is flagged for audit review, and ${flaggedFloorClause('document')}. `
      + 'Confirm the flag with the issuer of this document before relying on this analysis.';
  }
  /*
   * 'truth-disagreement' and 'audit-finding' SHARE this wording on purpose.
   * The noun is the neutral one both kinds satisfy — a truth-gate stamp and a
   * reviewer's section note are both quality findings — so the sentence needs
   * no second count and no per-kind rewrite. The in-app dialog differentiates
   * because it also names a REMEDY ("confirm what's true" vs "resolve the
   * findings"), and a remedy is exactly what this audience cannot execute.
   */
  const one = count === 1;
  return `Audit recommended — ${count} quality finding${one ? '' : 's'} ${one ? 'is' : 'are'} open on this analysis. `
    + `Confirm ${one ? 'its' : 'their'} status with the issuer of this document before relying on the statements ${one ? 'it affects' : 'they affect'}.`;
}

/** When the rules that decided "nothing stands" were in force, per audience. */
const BANNER_RULE_MOMENT = {
  /** Rendered inside the app, live, at page render (the reader's header
   *  status chip; a banner until 2026-10-13). */
  'in-app': 'the current rules',
  /** Written into a file that outlives the rules that produced it. */
  document: 'the rules in force at export',
} as const;

/** Which audience's rule-moment wording a title clause should carry. */
export type OpenAuditTitleAudience = keyof typeof BANNER_RULE_MOMENT;

/** The flagged-floor clause: the flag stands, nothing itemisable does. */
export function flaggedFloorClause(audience: OpenAuditTitleAudience = 'in-app'): string {
  return `no individual finding stands under ${BANNER_RULE_MOMENT[audience]}`;
}

/**
 * THE COUNT IN A TITLE — the heading-shaped sibling of the sentence above.
 *
 * Two surfaces put the number in a banner title rather than a sentence: the
 * reader's delivery state (`draftTierUx.bannerTitle` — rendered by DraftBanner
 * until 2026-10-13, now the detail of the report header's status chip)
 * and the main DOCX's amber cover heading (`reportDocxExport`). Both read
 * "Audit recommended — 1 finding to review" on a floor-of-1 `'flagged'` report,
 * which is the same untruth `openAuditDisclosureSentence` stopped telling: the
 * banner then renders NO bullets under it, and the DOCX cover prints no
 * findings beneath the heading, because there are none to print.
 *
 * Returns the clause AFTER the lead, not the whole title. The lead differs by
 * house casing — the reader banner's "Audit recommended", the DOCX cover's
 * "AUDIT RECOMMENDED" — and folding a casing decision into a shared string
 * would force one of them to post-process the other's bytes. What must not
 * differ is the part that makes a CLAIM, and that is all this returns. Both
 * call sites are locked against this builder at all three forms, each for its
 * own `audience` (flaggedFloorFourSurfaces.test.tsx).
 *
 * Null at zero / non-finite, on the same reasoning as the sentence: a clean
 * report may not grow a title from a stray flag. `flag === true` always yields
 * a count of at least 1, but that is an invariant of `openAuditFindings`, not
 * of this signature — callers handle the null rather than assert it away.
 *
 * ─── `audience` PICKS THE RULE-MOMENT CLAUSE, AND ONLY THAT ───────────
 *
 * Only the `'flagged'` branch names a moment at all, so `audience` changes one
 * clause of one branch; the itemised form is identical for both and the two
 * surfaces stay locked to each other there.
 *
 * The default is the IN-APP wording, and nothing in the type system makes a
 * document surface ask for the other one — a new exporter that forgets the
 * argument gets "the current rules" in a filed artifact. What catches that is
 * a test, not a signature: `flaggedFloorFourSurfaces.test.tsx` renders the
 * DOCX and asserts its cover carries `openAuditBannerSummary(audit,
 * 'document')` and does NOT carry the in-app form. A third title surface needs
 * its own row in that lock. (The argument is optional because `draftTierUx` —
 * the in-app caller — is the default audience; making it required would buy
 * nothing there and still not type-check the choice.)
 */
export function openAuditBannerSummary(
  audit: Pick<OpenAuditFindings, 'count' | 'kind'>,
  audience: OpenAuditTitleAudience = 'in-app',
): string | null {
  const { count, kind } = audit;
  if (!Number.isFinite(count) || count <= 0) return null;
  // The floor of 1 names no number: nothing is itemised, so "1 finding to
  // review" points the reader at a bullet list that will be empty.
  if (kind === 'flagged') return flaggedFloorClause(audience);
  return `${count} finding${count === 1 ? '' : 's'} to review`;
}

/**
 * WHERE THE LIVE LIST IS, per surface — the only clause of the frozen-decision
 * caption that may differ between them, held here so the two are edited
 * together instead of drifting apart in two exporters.
 *
 * Each destination carries its own grammatical NUMBER, because the verb after
 * it is not a function of how many destinations were named: "the Quality
 * Findings pages state" is plural on its own, "the cover disclosure states" is
 * not.
 */
const FROZEN_DECISION_LIVE_LISTS = {
  docx: {
    disclosure: { name: 'the cover disclosure', plural: false },
    findingsList: { name: 'the Report Quality section', plural: false },
  },
  pptx: {
    disclosure: { name: 'the Report Governance audit disclosure', plural: false },
    findingsList: { name: 'the Quality Findings pages', plural: true },
  },
} as const;

/** Surfaces that print `documentModel.qualityDecision.findings` verbatim. */
export type FrozenQualityDecisionSurface = keyof typeof FROZEN_DECISION_LIVE_LISTS;

/**
 * WHICH live-findings destinations THIS artifact actually contains.
 *
 * Both are conditional emissions, and on a flagged-floor report the findings
 * list is absent on both artifacts — the deck's "Quality Findings" pages are
 * emitted only for a non-empty list, and the DOCX's Report Quality section
 * bullets nothing. The caller passes the same expressions that GOVERN those
 * emissions; the caption may then name only what is there.
 */
export interface FrozenQualityDecisionLiveLists {
  /** The audit disclosure block was emitted (DOCX amber cover / deck governance row). */
  disclosure: boolean;
  /** At least one live finding was listed (DOCX Report Quality / deck Quality Findings). */
  findingsList: boolean;
}

/**
 * THE CAPTION over a frozen quality decision — one wording, both artifacts.
 *
 * In immutable-artifact mode a DOCX and a deck each carry TWO finding lists
 * resolved differently:
 *
 *   - the live one (`openAuditFindings` / `liveQualityHoldFindings`), which
 *     drops stamps today's rules explicitly supersede; and
 *   - `documentModel.qualityDecision.findings`, a REPLAY LOCK — the raw stamps
 *     frozen when the revision was sealed, reproduced byte-for-byte or the
 *     artifact stops replaying. It CANNOT be filtered here.
 *
 * On the Redhead probe (four superseded stamps plus one that still stands) the
 * live surfaces say 1 and the frozen list prints 5. That is not a defect once
 * each list says what it IS — which is all this caption does. Round 6 wrote it
 * on the DOCX only; the deck emitted the identical uncaptioned table, and its
 * own new governance disclosure made the contradiction visible there for the
 * first time. Shared so a third artifact cannot reintroduce the split.
 *
 * Emitted whenever the frozen list is non-empty, INCLUDING when the two lists
 * happen to agree: a caption that appears only on disagreement is itself a
 * signal the reader has to decode.
 *
 * ─── IT NAMES ONLY DESTINATIONS THE ARTIFACT CONTAINS ─────────────────
 *
 * Round 7 hardcoded both destination clauses. On a FLAGGED-FLOOR report — every
 * stamped finding superseded, so nothing live to itemise — the deck's caption
 * pointed at "the Quality Findings pages", which that deck does not emit
 * (`qualityFindings.length > 0` gates them), and the DOCX pointed at a Report
 * Quality section whose live bullets are empty. That is the same pointer defect
 * `openAuditListNote` lost twice: a sentence naming a place that is not there.
 * `live` says which destinations were actually emitted, and the clause is built
 * from that — including the case where NEITHER was, where the caption states
 * the frozen list's status and points nowhere at all.
 *
 * ─── THE NEITHER-DESTINATION BRANCH IS ORDINARY, NOT THEORETICAL ──────
 *
 * Round 8 certified that branch as builder-only ("neither exporter currently
 * reaches it"). That was wrong, and wrong on an ordinary report: the frozen
 * list does NOT come from the audit flag. `buildQualityDecision`
 * (buildReportDocumentV1.ts) seeds it from raw `qualityHoldFindings`, reviewer
 * issues, failed error-severity `sanityChecks`, and a synthesized row for a
 * `'not-run'` reviewer verdict. The live Report Quality section bullets
 * `sanityFindings` — a DIFFERENT array — so a report with one failed sanity
 * CHECK and no sanity FINDING, flagged by nothing, seals a one-row frozen
 * decision with no disclosure and no live list beside it: both `live` flags
 * false, this branch, through the real exporters. Covered at the ARTIFACT level
 * in `reportDocxExport.immutableQualityReconciliation.test.ts` and
 * `reportPptxExport.immutableQualityReconciliation.test.ts`.
 *
 * (The `'not-run'` row is not a second path: that document state is
 * `'degraded'`, which `assertExportableTier` bars outright — asserted in the
 * DOCX test beside the case above.)
 *
 * The same correction sizes round 8's byte-change claim ("only a flagged-floor
 * report"): the caption's wording moved for every report whose frozen list is
 * non-empty and whose two live-list gates are not BOTH true — the flagged-floor
 * report, the failed-sanity-check report above, and any mixture. Only a report
 * that emits both live lists kept round 7's bytes.
 */
export function frozenQualityDecisionCaption(
  frozenCount: number,
  surface: FrozenQualityDecisionSurface,
  live: FrozenQualityDecisionLiveLists,
): string | null {
  if (!Number.isFinite(frozenCount) || frozenCount <= 0) return null;
  const one = frozenCount === 1;
  const names = FROZEN_DECISION_LIVE_LISTS[surface];
  const present = [
    ...(live.disclosure ? [names.disclosure] : []),
    ...(live.findingsList ? [names.findingsList] : []),
  ];
  const sealed = `The ${frozenCount} finding${one ? '' : 's'} below `
    + `${one ? 'is' : 'are'} the quality decision `
    + 'AS SEALED when this revision was frozen, reproduced verbatim and not re-evaluated. ';
  if (present.length === 0) {
    /*
     * No live list accompanies the frozen one, so there is no "there" for the
     * contrast clause to point at. The wording states exactly what the `live`
     * flags encode — this artifact carries no live findings list — and nothing
     * beyond it. It deliberately does NOT say that nothing still stands, nor
     * that every frozen row was superseded: what came back false is the
     * emitter's own gates, and a report can reach this branch with live
     * findings this artifact simply does not print.
     */
    return `${sealed}This document carries no live findings list beside it, so this list must `
      + 'not be read as the current one: a finding listed here may have been superseded by a '
      + 'rule change rather than resolved.';
  }
  const clause = present.map(({ name }) => name).join(' and ');
  // Plural when more than one destination is named, or when the single named
  // destination is itself plural ("the Quality Findings pages state").
  const plural = present.length > 1 || present.some((destination) => destination.plural);
  return `${sealed}${clause.charAt(0).toUpperCase()}${clause.slice(1)} `
    + `state${plural ? '' : 's'} what still stands under the `
    + 'rules in force at export, so the two lists can differ in content and in count: a '
    + 'finding listed here but absent there was superseded by a rule change, not resolved, '
    + 'and neither list supersedes the other.';
}
